import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export interface InternalTransfer {
  id: string;
  transfer_date: string;
  from_account_id: string;
  to_account_id: string;
  from_amount: number;
  to_amount: number;
  from_currency: string;
  to_currency: string;
  exchange_rate: number | null;
  exchange_rate_source: string | null;
  reference_number: string | null;
  description: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateTransferInput {
  transfer_date: string;
  from_account_id: string;
  to_account_id: string;
  from_amount: number;
  to_amount: number;
  from_currency: string;
  to_currency: string;
  exchange_rate?: number | null;
  exchange_rate_source?: string;
  reference_number?: string;
  description?: string;
}

const TRANSFER_FEE_CATEGORY_ID = 'c5b646db-d39f-4b71-bf74-e9e07616039c';

async function createFeeExpense(
  input: CreateTransferInput,
  feeAmount: number,
  userId: string | undefined
) {
  // Generate expense number
  const { data: expNumData, error: expNumError } = await supabase.rpc('generate_expense_number');
  if (expNumError) {
    console.error('Failed to generate expense number for transfer fee:', expNumError);
    return;
  }

  const { error } = await supabase.from('expenses').insert({
    expense_number: expNumData as string,
    expense_date: input.transfer_date,
    payment_date: input.transfer_date,
    description: `Transfer fee: ${input.reference_number || 'N/A'}`,
    amount: feeAmount,
    vat_amount: 0,
    total_amount: feeAmount,
    currency: input.from_currency,
    bank_account_id: input.from_account_id,
    payment_status: 'paid',
    payment_method: 'bank_transfer',
    reference_number: input.reference_number || null,
    category_id: TRANSFER_FEE_CATEGORY_ID,
    created_by: userId || null,
  });

  if (error) {
    console.error('Failed to create transfer fee expense:', error);
    toast.error('Transfer recorded but fee expense creation failed: ' + error.message);
  }
}

export function useInternalTransfers() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const transfersQuery = useQuery({
    queryKey: ['internal-transfers'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('internal_transfers' as any)
        .select('*')
        .order('transfer_date', { ascending: false });
      if (error) throw error;
      return (data || []) as unknown as InternalTransfer[];
    },
    enabled: !!user,
  });

  const createMutation = useMutation({
    mutationFn: async (input: CreateTransferInput) => {
      const { data, error } = await supabase
        .from('internal_transfers' as any)
        .insert({ ...input, created_by: user?.id })
        .select()
        .single();
      if (error) throw error;

      // Auto-create fee expense if same currency and from_amount > to_amount
      if (input.from_currency === input.to_currency && input.from_amount > input.to_amount) {
        const feeAmount = parseFloat((input.from_amount - input.to_amount).toFixed(2));
        if (feeAmount > 0) {
          await createFeeExpense(input, feeAmount, user?.id);
        }
      }

      return data as unknown as InternalTransfer;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['internal-transfers'] });
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      toast.success('Transfer recorded successfully');
    },
    onError: (error: any) => {
      toast.error('Failed to record transfer: ' + error.message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('internal_transfers' as any)
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['internal-transfers'] });
      toast.success('Transfer deleted');
    },
    onError: (error: any) => {
      toast.error('Failed to delete transfer: ' + error.message);
    },
  });

  return {
    transfers: transfersQuery.data || [],
    isLoading: transfersQuery.isLoading,
    createTransfer: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    deleteTransfer: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
  };
}
